/** Utilidades de presentacion de la jornada (reloj, horas, desviaciones). */

export const DAY_NAMES = ['Domingo', 'Lunes', 'Martes', 'Miércoles', 'Jueves', 'Viernes', 'Sábado'];
export const DAY_NAMES_SHORT = ['Dom', 'Lun', 'Mar', 'Mié', 'Jue', 'Vie', 'Sáb'];
/** Orden de presentacion: lunes a domingo. */
export const DAY_ORDER = [1, 2, 3, 4, 5, 6, 0];

/** 3725 -> "01:02:05" */
export function formatClock(totalSeconds: number): string {
  const s = Math.max(0, Math.floor(totalSeconds));
  const h = Math.floor(s / 3600);
  const m = Math.floor((s % 3600) / 60);
  const sec = s % 60;
  return [h, m, sec].map((n) => String(n).padStart(2, '0')).join(':');
}

/** "1,2,3,4,5" -> [1,2,3,4,5] */
export function parseDays(csv: string): number[] {
  return [...new Set(csv.split(',').map((x) => Number(x.trim())).filter((n) => Number.isInteger(n) && n >= 0 && n <= 6))].sort((a, b) => a - b);
}
export function daysToCsv(days: number[]): string {
  return [...new Set(days)].sort((a, b) => a - b).join(',');
}
/** [1,2,3,4,5] -> "Lun–Vie"; salteados -> "Lun, Mié, Vie". */
export function describeDays(days: number[]): string {
  const sorted = DAY_ORDER.filter((d) => days.includes(d));
  if (sorted.length === 0) return '—';
  const idx = sorted.map((d) => DAY_ORDER.indexOf(d));
  const consecutive = idx.every((v, i) => i === 0 || v === idx[i - 1] + 1);
  if (consecutive && sorted.length > 2) return `${DAY_NAMES_SHORT[sorted[0]]}–${DAY_NAMES_SHORT[sorted[sorted.length - 1]]}`;
  return sorted.map((d) => DAY_NAMES_SHORT[d]).join(', ');
}

/** Minutos desde la medianoche en la zona horaria dada, para un instante. */
export function minutesOfDayIn(timeZone: string, date = new Date()): number {
  try {
    const parts = new Intl.DateTimeFormat('en-US', { timeZone, hour: '2-digit', minute: '2-digit', hourCycle: 'h23' }).formatToParts(date);
    const get = (t: string) => Number(parts.find((p) => p.type === t)?.value ?? 0);
    return get('hour') * 60 + get('minute');
  } catch {
    return date.getHours() * 60 + date.getMinutes();
  }
}

export function hmToMinutes(hm: string): number {
  const [h, m] = hm.split(':').map(Number);
  return (h || 0) * 60 + (m || 0);
}

/** Diferencia (minutos) entre la hora actual y una hora objetivo "HH:MM", normalizada a (-720, 720]. + = despues de la hora objetivo. */
export function deviationFrom(targetHm: string, timeZone: string, now = new Date()): number {
  let diff = minutesOfDayIn(timeZone, now) - hmToMinutes(targetHm);
  while (diff > 720) diff -= 1440;
  while (diff <= -720) diff += 1440;
  return diff;
}

export function formatMinutes(total: number): string {
  const m = Math.abs(Math.round(total));
  const h = Math.floor(m / 60);
  const r = m % 60;
  if (h === 0) return `${r} min`;
  return r === 0 ? `${h} h` : `${h} h ${r} min`;
}

/** Hora corta local de un ISO: "8:02 a. m." */
export function shortTime(iso: string, timeZone?: string): string {
  return new Date(iso).toLocaleTimeString('es-CO', { hour: '2-digit', minute: '2-digit', ...(timeZone ? { timeZone } : {}) });
}
export function shortDateTime(iso: string, timeZone?: string): string {
  return new Date(iso).toLocaleString('es-CO', { dateStyle: 'short', timeStyle: 'short', ...(timeZone ? { timeZone } : {}) });
}

/** ISO -> valor para <input type="datetime-local"> (hora local del dispositivo). */
export function toDatetimeLocal(iso: string | Date): string {
  const d = typeof iso === 'string' ? new Date(iso) : iso;
  const p = (n: number) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${p(d.getMonth() + 1)}-${p(d.getDate())}T${p(d.getHours())}:${p(d.getMinutes())}`;
}
